import { Checkbox, Grid, Typography } from "@mui/material";
import { FormControlLabel } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Moment } from "moment";
import { Controller, useForm } from "react-hook-form";
import { EndFilter, StartFilter } from "../../../shared/StartEndFilter";
import { filtersStyles } from "../../styles/filtersStyles";

type FilterFormInputs = {
  startDate: Moment;
  endDate: Moment;
  includeMicrodelays: boolean;
  includeUndeclaredDelays: boolean;
};

type Props = {
  onFilterChange: (startDate: Moment, endDate: Moment) => void;
  initialStartDate: Moment;
  initialEndDate: Moment;
  includeMicrodelays: boolean;
  includeUndeclaredDelays: boolean;
  onIncludeMicrodelaysChange: (include: boolean) => void;
  onIncludeUndeclaredDelaysChange: (include: boolean) => void;
};

export const Filters = ({
  onFilterChange,
  initialStartDate,
  initialEndDate,
  includeMicrodelays,
  includeUndeclaredDelays,
  onIncludeMicrodelaysChange,
  onIncludeUndeclaredDelaysChange,
}: Props) => {
  const theme = useTheme();
  const styles = filtersStyles(theme);

  const {
    control,
    formState: { errors },
    setValue,
    getValues,
  } = useForm<FilterFormInputs>({
    defaultValues: {
      startDate: initialStartDate,
      endDate: initialEndDate,
      includeMicrodelays: includeMicrodelays,
      includeUndeclaredDelays: includeUndeclaredDelays,
    },
    mode: "onChange",
  });

  const handleDateChange = async (
    date: Moment | null,
    fieldName: "startDate" | "endDate",
  ) => {
    if (date) {
      setValue(fieldName, date, { shouldValidate: true });

      const { startDate, endDate } = getValues();

      if (startDate && endDate && startDate.isBefore(endDate)) {
        onFilterChange(startDate, endDate);
      }
    }
  };

  const handleCheckboxChange = (
    field: "includeMicrodelays" | "includeUndeclaredDelays",
    e: { target: { checked: boolean } },
  ) => {
    setValue(field, e.target.checked);

    const { startDate, endDate } = getValues();

    if (startDate && endDate && startDate.isBefore(endDate)) {
      field === "includeMicrodelays"
        ? onIncludeMicrodelaysChange(e.target.checked)
        : onIncludeUndeclaredDelaysChange(e.target.checked);
    }
  };

  return (
    <Grid item container spacing={2} alignItems="center">
      <Grid item>
        <Controller
          name="startDate"
          control={control}
          rules={{
            required: "Start date is required",
            validate: (value, formValue) => {
              if (!value) return "Start date is required";
              if (formValue && !value.isBefore(formValue.endDate)) {
                return "Start date must be before end date";
              }
              return true;
            },
          }}
          render={({ field }) => (
            <StartFilter
              {...field}
              onChange={(date) => handleDateChange(date, "startDate")}
            />
          )}
        />
        {errors.startDate && (
          <Typography sx={styles.errorMessage}>
            {errors.startDate.message}
          </Typography>
        )}
      </Grid>
      <Grid item>
        <Controller
          name="endDate"
          control={control}
          rules={{
            required: "End date is required",
            validate: (value, formValue) => {
              if (!value) return "End date is required";
              if (formValue && !value.isAfter(formValue.startDate)) {
                return "End date must be after start date";
              }
              return true;
            },
          }}
          render={({ field }) => (
            <EndFilter
              {...field}
              onChange={(date) => handleDateChange(date, "endDate")}
            />
          )}
        />
        {errors.endDate && (
          <Typography sx={styles.errorMessage}>
            {errors.endDate.message}
          </Typography>
        )}
      </Grid>
      <Grid item>
        <Controller
          name="includeMicrodelays"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeMicrodelays}
                  color="primary"
                  onChange={(e) =>
                    handleCheckboxChange("includeMicrodelays", e)
                  }
                />
              }
              label="Microdelays"
              style={{ marginBottom: 0, marginLeft: 1 }}
            />
          )}
        />
      </Grid>
      <Grid item>
        <Controller
          name="includeUndeclaredDelays"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeUndeclaredDelays}
                  color="primary"
                  onChange={(e) =>
                    handleCheckboxChange("includeUndeclaredDelays", e)
                  }
                />
              }
              label="Undeclared Delays"
              style={{ marginBottom: 0, marginLeft: 1 }}
            />
          )}
        />
      </Grid>
    </Grid>
  );
};
